import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ShoppingBag, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';

const WHATSAPP_NUMBER = '917880399081'; // country code + number

const buildWhatsappLink = (order) => {
  const lines = [
    `*New Order — Ecom Seekho*`,
    `Order ID: ${order.id}`,
    ``,
    `*Customer:* ${order.customer}`,
    `*Phone:* ${order.phone}`,
    order.email ? `*Email:* ${order.email}` : null,
    `*Address:* ${order.address}`,
    order.notes ? `*Notes:* ${order.notes}` : null,
    ``,
    `*Items:*`,
    ...order.items.map((i) => `• ${i.name} × ${i.qty} — ₹${(i.price * i.qty).toFixed(2)}`),
    ``,
    `*Total: ₹${order.total.toFixed(2)}* (incl. GST)`,
    ``,
    `Please confirm the order and share payment details.`,
  ]
    .filter(Boolean)
    .join('\n');
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines)}`;
};

const Checkout = () => {
  const { items, subtotal, gst, total, clearCart, getUnitPrice } = useCart();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [placed, setPlaced] = useState(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    pincode: '',
    notes: '',
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handlePlace = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.city || !form.pincode) {
      toast({ title: 'Please fill all required fields', variant: 'destructive' });
      return;
    }
    const orderId = 'ORD' + Math.floor(1000 + Math.random() * 9000);
    const order = {
      id: orderId,
      customer: form.name,
      phone: form.phone,
      email: form.email,
      address: `${form.address}, ${form.city} - ${form.pincode}`,
      notes: form.notes,
      items: items.map((i) => ({
        id: i.id,
        name: i.name,
        qty: i.qty,
        price: getUnitPrice(i, i.qty),
      })),
      total,
      status: 'pending',
      paymentMethod: 'WhatsApp',
      date: new Date().toISOString().slice(0, 10),
    };
    const existing = JSON.parse(localStorage.getItem('es_orders') || '[]');
    localStorage.setItem('es_orders', JSON.stringify([order, ...existing]));
    setPlaced(order);
    clearCart();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Auto-open WhatsApp in a new tab
    const url = buildWhatsappLink(order);
    setTimeout(() => {
      window.open(url, '_blank', 'noopener,noreferrer');
    }, 400);
  };

  if (placed) {
    const waUrl = buildWhatsappLink(placed);
    return (
      <div className="bg-gray-50 min-h-screen py-16">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-lg border border-emerald-200 p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-emerald-700" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Thank You!</h2>
            <p className="text-gray-600 mt-2">
              Hello <span className="font-semibold">{placed.customer}</span>, your order{' '}
              <span className="font-semibold text-emerald-700">#{placed.id}</span> has been received.
            </p>

            <div className="mt-6 rounded-lg border-2 border-green-500 bg-green-50 p-5">
              <MessageCircle className="w-10 h-10 text-green-600 mx-auto mb-2" />
              <h3 className="font-bold text-green-800">Complete Payment via WhatsApp</h3>
              <p className="text-sm text-gray-700 mt-1">
                Please message our team on WhatsApp to confirm the order and receive payment details.
              </p>
              <p className="text-lg font-bold text-green-700 mt-2">+91 78803 99081</p>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 bg-green-500 hover:bg-green-600 text-white font-semibold px-5 py-2.5 rounded-md transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
              </a>
              <p className="text-[10px] text-gray-500 mt-2">
                Didn't open automatically? Click the button above.
              </p>
            </div>

            <div className="mt-6 text-left bg-gray-50 rounded p-4 text-sm">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Order Total</span>
                <span className="font-bold text-emerald-700">₹{placed.total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Payment Method</span>
                <span>WhatsApp</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Ship to</span>
                <span className="text-right max-w-[60%]">{placed.address}</span>
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-center">
              <Link to="/">
                <Button variant="outline">Back to Home</Button>
              </Link>
              <Link to="/shop">
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-16 text-center">
        <ShoppingBag className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-600">Your cart is empty.</p>
        <Link to="/shop">
          <Button className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white">Shop Now</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Checkout</h1>

        <form onSubmit={handlePlace} className="grid md:grid-cols-[1fr_360px] gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
            <h2 className="font-semibold text-gray-800">Delivery Details</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input id="name" name="name" value={form.name} onChange={handleChange} required />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input id="phone" name="phone" value={form.phone} onChange={handleChange} required />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="email">Email (optional)</Label>
                <Input id="email" type="email" name="email" value={form.email} onChange={handleChange} />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="address">Address *</Label>
                <Textarea id="address" name="address" value={form.address} onChange={handleChange} required rows={3} />
              </div>
              <div>
                <Label htmlFor="city">City *</Label>
                <Input id="city" name="city" value={form.city} onChange={handleChange} required />
              </div>
              <div>
                <Label htmlFor="pincode">Pincode *</Label>
                <Input id="pincode" name="pincode" value={form.pincode} onChange={handleChange} required />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="notes">Order Notes (optional)</Label>
                <Textarea id="notes" name="notes" value={form.notes} onChange={handleChange} rows={2} />
              </div>
            </div>

            <div className="pt-4 border-t">
              <h2 className="font-semibold text-gray-800 mb-2">Payment Method</h2>
              <label className="flex items-center gap-3 p-3 border-2 border-green-500 bg-green-50 rounded cursor-pointer">
                <input type="radio" checked readOnly className="accent-green-600" />
                <MessageCircle className="w-5 h-5 text-green-600" />
                <div>
                  <div className="font-medium text-green-800">Pay via WhatsApp</div>
                  <div className="text-xs text-gray-600">
                    You'll be redirected to our WhatsApp (+91 78803 99081) to confirm the order and share payment details.
                  </div>
                </div>
              </label>
            </div>
          </div>

          <aside>
            <div className="bg-white rounded-lg border border-gray-200 p-5 sticky top-32">
              <h3 className="font-semibold text-gray-800 mb-4">Your Order</h3>
              <div className="space-y-2 text-sm max-h-56 overflow-y-auto pr-1">
                {items.map((i) => (
                  <div key={i.id} className="flex justify-between gap-2">
                    <span className="text-gray-700 line-clamp-1">
                      {i.name} <span className="text-gray-400">× {i.qty}</span>
                    </span>
                    <span className="font-medium text-gray-800 flex-shrink-0">
                      ₹{(getUnitPrice(i, i.qty) * i.qty).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="border-t mt-4 pt-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span>₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">GST (18%)</span>
                  <span>₹{gst.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span className="text-emerald-700">₹{total.toFixed(2)}</span>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full mt-5 bg-green-500 hover:bg-green-600 text-white flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" /> Place Order via WhatsApp
              </Button>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
